<script setup lang="ts">
    import { type SharedData, type AuthUser } from '@/types';
    import { useForm, usePage, Link, router } from '@inertiajs/vue3';
    import { Button } from '@/components/ui/button';
    import { Input } from '@/components/ui/input';
    import { InputRow } from '@/components/ui/form';
    import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
    import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
    import { useInitials } from '@/composables/useInitials';
    import { Separator } from '@/components/ui/separator';
    import { Select } from '@/components/ui/form';
    import { optionsOnOff } from '@/utils/form';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { computed, onErrorCaptured, ref } from 'vue';
    import { LogOut, Pencil } from 'lucide-vue-next';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';

    const props = defineProps<{
        user: AuthUser;
    }>();

    const { t, locale } = useI18n({ useScope: 'global' });

    const { getInitials } = useInitials();

    const form = useForm({
        name: props.user.name,
        email: props.user.email,
        phone: props.user.phone,
        language: props.user.language,
        receive_notifications: props.user.receive_notifications,
    });

    const languageOptions = computed(() => [
        { value: 'da', label: t('users.language.da') },
        { value: 'en', label: t('users.language.en') },
    ]);

    const submit = () => {
        form.patch(route('v2.profile.update'), {
            preserveScroll: true,
            onSuccess: () => {
                locale.value = form.language;
                toast.success(t('forms.general.success'));
            },
        });
    };

    const fileInput = ref<HTMLInputElement | null>(null);

    const triggerFileInput = () => {
        fileInput.value?.click();
    };

    const { mutate: uploadProfilePicture, isPending } = useMutation({
        mutationFn: (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            return axios.post(route('v2.user.profile-picture.upload'), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            router.reload();
        },
        onError: (error: any) => {
            if (error.status === 413) {
                toast.error(t('profile.picture_upload_error'));
            } else {
                toast.error(t('profile.picture_upload_failed'));
            }
        },
    });

    const handleFileChange = (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files[0]) {
            uploadProfilePicture(target.files[0]);
            target.value = '';
        }
    };

    const handleLogout = () => {
        router.flushAll();
        router.post(route('logout'));
    };
</script>

<template>
    <Card class="w-full max-w-lg self-center">
        <CardHeader>
            <div class="flex items-center gap-2">
                <!-- <button
                    type="button"
                    class="group relative cursor-pointer overflow-hidden rounded-full"
                    @click="triggerFileInput"
                    :disabled="isPending"
                > -->
                <Avatar :class="[isPending && 'opacity-50']">
                    <AvatarImage
                        v-if="user.avatar"
                        :src="user.avatar"
                        :alt="user.name"
                    />
                    <AvatarFallback>
                        {{ getInitials(user?.name) }}
                    </AvatarFallback>
                    <span
                        class="bg-foreground/50 pointer-events-none absolute inset-0 flex items-center justify-center text-white opacity-0 transition-opacity group-hover:opacity-100"
                        v-if="!isPending"
                    >
                        <Pencil class="size-4" />
                    </span>
                </Avatar>
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFileChange"
                />
                <!-- </button> -->
                <CardTitle>{{ t('profile.your_information') }}</CardTitle>
                <Button
                    variant="ghost_primary"
                    size="sm"
                    width="narrow"
                    class="ml-auto"
                    @click="handleLogout"
                >
                    {{ t('menu.logout') }}
                    <LogOut />
                </Button>
            </div>
        </CardHeader>
        <form @submit.prevent="submit">
            <CardContent class="space-y-5">
                <InputRow
                    :form="form"
                    name="name"
                    label="users.form.name"
                >
                    <Input
                        id="name"
                        v-model="form.name"
                        required
                        autocomplete="name"
                        :placeholder="t('users.form.name')"
                    />
                </InputRow>

                <InputRow
                    :form="form"
                    name="email"
                    label="users.form.email"
                >
                    <Input
                        id="email"
                        type="email"
                        v-model="form.email"
                        required
                        autocomplete="username"
                        :placeholder="t('users.form.email')"
                    />
                </InputRow>

                <InputRow
                    :form="form"
                    name="phone"
                    label="users.form.phone"
                >
                    <Input
                        id="phone"
                        type="tel"
                        v-model="form.phone"
                        autocomplete="tel"
                        :placeholder="t('users.form.phone')"
                    />
                </InputRow>
                <Separator />

                <InputRow
                    :form="form"
                    name="language"
                    label="users.form.language"
                >
                    <Select
                        v-model="form.language"
                        :options="languageOptions"
                    />
                </InputRow>

                <InputRow
                    :form="form"
                    name="receive_notifications"
                    label="users.form.notifications"
                >
                    <Select
                        v-model="form.receive_notifications"
                        :options="optionsOnOff"
                        :translate="true"
                    />
                </InputRow>
            </CardContent>
            <CardFooter class="border-t">
                <Button
                    :disabled="form.processing || !form.isDirty"
                    :loading="form.processing"
                >
                    {{ t('forms.save_changes') }}
                </Button>
            </CardFooter>
        </form>
    </Card>
</template>
